import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft,
  ChevronRight,
  Eye,
  EyeOff,
  ImagePlus,
  Lock,
  ShieldCheck,
  Trash2,
  UserCircle2,
  UserRound,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../../components/commoncomponents/ConfirmationModal';
import { resolveApiAssetUrl } from '../../../services/api';
import { changePassword, updateProfile } from '../../../services/authService';

const settingsSections = [
  {
    key: 'profile',
    title: 'Profile',
    description: 'Update your account details and display image.',
    icon: UserRound,
  },
  {
    key: 'security',
    title: 'Account Security',
    description: 'Manage password and sign-in protection.',
    icon: Lock,
  },
];

function SectionCard({ description, icon: Icon, onClick, title }) {
  return (
    <button
      className="flex w-full items-center gap-3 rounded-[22px] border border-[#e6ddd4] bg-white px-4 py-3.5 text-left shadow-[0_8px_20px_rgba(17,17,17,0.04)] transition hover:-translate-y-0.5 hover:border-[#d7cbc0] hover:shadow-[0_12px_24px_rgba(17,17,17,0.07)]"
      type="button"
      onClick={onClick}
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[18px] bg-[#f2ede7] text-[#6f6258]">
        <Icon className="h-4.5 w-4.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[15px] font-semibold text-[#1f293d] sm:text-base">{title}</div>
        <div className="mt-0.5 text-[13px] text-[#6f7f95] sm:text-sm">{description}</div>
      </div>
      <ChevronRight className="h-4.5 w-4.5 shrink-0 text-[#98a7bc]" />
    </button>
  );
}

function PasswordField({
  description,
  name,
  onChange,
  onToggleVisibility,
  placeholder,
  type,
  value,
  visible,
}) {
  return (
    <label className="grid gap-2 rounded-[18px] border border-[#e6ddd4] bg-white px-3.5 py-3 shadow-[0_8px_20px_rgba(17,17,17,0.04)]">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[16px] bg-[#f2ede7] text-[#7b879b]">
          <Lock className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <span className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#98a7bc]">
            {description}
          </span>
          <div className="relative mt-2">
            <input
              className="w-full border-0 bg-transparent pr-10 text-[15px] text-[#1f293d] outline-none placeholder:text-[#a5b0bf] sm:text-base"
              type={visible ? 'text' : type}
              name={name}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
            />
            <button
              className="absolute right-0 top-1/2 -translate-y-1/2 rounded-full p-1 text-[#98a7bc] transition hover:bg-[#f8f4f0]"
              type="button"
              onClick={() => onToggleVisibility(name)}
              aria-label={visible ? `Hide ${name}` : `Show ${name}`}
            >
              {visible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>
    </label>
  );
}

function ProfileModal({ isOpen, user, token, onClose, onSaveProfile, onPasswordChanged }) {
  const wasOpenRef = useRef(false);
  const [activeSection, setActiveSection] = useState('menu');
  const [accountName, setAccountName] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [profileMessage, setProfileMessage] = useState('');
  const [profileError, setProfileError] = useState('');
  const [profileSubmitting, setProfileSubmitting] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [passwordValues, setPasswordValues] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }

    if (wasOpenRef.current) {
      return;
    }

    wasOpenRef.current = true;

    setActiveSection('menu');
    setPasswordValues({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setPasswordVisibility({
      currentPassword: false,
      newPassword: false,
      confirmPassword: false,
    });
    setAccountName(user?.username || '');
    setAvatarPreview(resolveApiAssetUrl(user?.avatar));
    setAvatarFile(null);
    setProfileMessage('');
    setProfileError('');
    setPasswordMessage('');
    setPasswordError('');
  }, [isOpen, user?.avatar, user?.username]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const { body } = document;
    const previousOverflow = body.style.overflow;

    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const hasUnsavedChanges =
    accountName.trim() !== (user?.username || '') ||
    avatarPreview !== resolveApiAssetUrl(user?.avatar) ||
    Boolean(
      passwordValues.currentPassword ||
        passwordValues.newPassword ||
        passwordValues.confirmPassword
    );

  function requestClose() {
    if (hasUnsavedChanges) {
      setIsDiscardModalOpen(true);
      return;
    }

    onClose();
  }

  function confirmDiscardChanges() {
    setIsDiscardModalOpen(false);
    onClose();
  }

  function handleAvatarUpload(event) {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    if (!selectedFile.type.startsWith('image/')) {
      setProfileError('Please choose an image file only.');
      return;
    }

    if (selectedFile.size > 2 * 1024 * 1024) {
      setProfileError('Profile image must be 2 MB or smaller.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setAvatarFile(selectedFile);
      setAvatarPreview(typeof reader.result === 'string' ? reader.result : '');
      setProfileError('');
      setProfileMessage('');
    };
    reader.readAsDataURL(selectedFile);
  }

  function handleRemoveAvatar() {
    setAvatarFile(null);
    setAvatarPreview('');
    setProfileError('');
    setProfileMessage('');
  }

  async function handleProfileSubmit(event) {
    event.preventDefault();
    setProfileMessage('');
    setProfileError('');

    const nextUsername = accountName.trim();

    if (!nextUsername) {
      setProfileError('Account name is required.');
      return;
    }

    setProfileSubmitting(true);

    try {
      const profilePayload = new FormData();

      profilePayload.append('username', nextUsername);

      if (avatarFile) {
        profilePayload.append('avatar', avatarFile);
      }

      if (!avatarPreview && user?.avatar) {
        profilePayload.append('remove_avatar', 'true');
      }

      const response = await updateProfile(token, profilePayload);

      onSaveProfile({
        token: response.token,
        user: response.user,
      });
      setAccountName(response.user?.username || nextUsername);
      setAvatarPreview(resolveApiAssetUrl(response.user?.avatar));
      setAvatarFile(null);
      const successMessage = response.message || 'Settings updated successfully.';
      setProfileMessage(successMessage);
      toast.success(successMessage);
    } catch (requestError) {
      setProfileError(requestError.message);
    } finally {
      setProfileSubmitting(false);
    }
  }

  function handlePasswordChange(event) {
    const { name, value } = event.target;
    setPasswordValues((currentValue) => ({
      ...currentValue,
      [name]: value,
    }));
  }

  function togglePasswordVisibility(fieldName) {
    setPasswordVisibility((currentValue) => ({
      ...currentValue,
      [fieldName]: !currentValue[fieldName],
    }));
  }

  async function handlePasswordSubmit(event) {
    event.preventDefault();
    setPasswordMessage('');
    setPasswordError('');

    if (
      !passwordValues.currentPassword ||
      !passwordValues.newPassword ||
      !passwordValues.confirmPassword
    ) {
      setPasswordError('Please complete all password fields.');
      return;
    }

    if (passwordValues.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }

    if (passwordValues.newPassword !== passwordValues.confirmPassword) {
      setPasswordError('New password and confirm password do not match.');
      return;
    }

    setPasswordSubmitting(true);

    try {
      const response = await changePassword(token, {
        current_password: passwordValues.currentPassword,
        new_password: passwordValues.newPassword,
        confirm_password: passwordValues.confirmPassword,
      });

      setPasswordValues({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      const successMessage = response.message || 'Settings updated successfully.';
      setPasswordMessage(successMessage);
      toast.success(successMessage);
      onPasswordChanged({
        token: response.token,
        user: response.user,
      });
    } catch (requestError) {
      setPasswordError(requestError.message);
    } finally {
      setPasswordSubmitting(false);
    }
  }

  function renderSettingsMenu() {
    return (
      <div className="grid gap-3">
        <div className="rounded-[22px] border border-[#efe7df] bg-white px-5 py-3 shadow-[0_8px_20px_rgba(17,17,17,0.04)]">
          <div className="text-[15px] font-semibold text-[#1f293d] sm:text-[16px]">Choose a settings area</div>
          <p className="mt-1 text-[13px] leading-5.5 text-[#6f7f95] sm:text-sm">
            Each section opens in a cleaner workspace so updates feel easier to manage.
          </p>
        </div>

        <div className="grid gap-3.5">
          {settingsSections.map((section) => (
            <SectionCard
              key={section.key}
              title={section.title}
              description={section.description}
              icon={section.icon}
              onClick={() => setActiveSection(section.key)}
            />
          ))}
        </div>
      </div>
    );
  }

  function renderProfileSection() {
    return (
      <div className="rounded-[22px] border border-[#efe7df] bg-[#fbf9f6] p-3.5 shadow-[0_10px_22px_rgba(17,17,17,0.04)] sm:p-4">
        <div className="rounded-[20px] border border-[#e6ddd4] bg-white px-4 py-2.5 shadow-[0_8px_18px_rgba(17,17,17,0.04)]">
          <div className="flex items-center gap-3">
            <button
              className="flex h-9 w-9 items-center justify-center rounded-[16px] border border-[#e6ddd4] text-[#7f8fa5] transition hover:bg-[#faf7f3]"
              type="button"
              onClick={() => setActiveSection('menu')}
              aria-label="Back to settings sections"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <h4 className="text-[15px] font-semibold text-[#1f293d] sm:text-[16px]">Profile</h4>
              <p className="text-[13px] text-[#6f7f95] sm:text-sm">Update your account details and display image.</p>
            </div>
          </div>
        </div>

        <form className="mt-3.5 grid gap-3" onSubmit={handleProfileSubmit}>
          <div className="rounded-[22px] border border-[#e6ddd4] bg-white p-3.5 shadow-[0_8px_20px_rgba(17,17,17,0.04)] sm:p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex justify-center">
                {avatarPreview ? (
                  <img
                    className="h-20 w-20 rounded-full border border-[#e6ddd4] object-cover shadow-[0_8px_18px_rgba(17,17,17,0.08)] sm:h-24 sm:w-24"
                  src={resolveApiAssetUrl(avatarPreview)}
                    alt={`${user?.username} avatar`}
                  />
                ) : (
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#f2ede7] text-[#6f6258] sm:h-24 sm:w-24">
                    <UserCircle2 className="h-8 w-8 sm:h-10 sm:w-10" />
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <label className="block">
                  <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8a98ad]">
                    Account
                  </span>
                  <input
                    className="w-full rounded-[16px] border border-[#e6ddd4] bg-[#f8f5f1] px-4 py-2.5 text-[13px] font-semibold text-[#1f293d] outline-none transition focus:border-[#cdb9a8] focus:bg-white sm:text-sm"
                    type="text"
                    value={accountName}
                    onChange={(event) => {
                      setAccountName(event.target.value);
                      setProfileError('');
                      setProfileMessage('');
                    }}
                    placeholder="Enter account name"
                  />
                </label>
                <p className="mt-2 text-[13px] leading-5.5 text-[#6f7f95] sm:text-sm">
                  Update your account name and photo so your account menu and workspace feel more personal.
                </p>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <label className="cursor-pointer rounded-[18px] border border-dashed border-[#d8d1ca] bg-[#fcfbf9] px-4 py-2.5 text-[13px] font-semibold text-[#6f5644] transition hover:bg-[#faf7f3] sm:text-sm">
                    <input className="hidden" type="file" accept="image/*" onChange={handleAvatarUpload} />
                    <span className="flex items-center justify-center gap-2">
                      <ImagePlus className="h-4 w-4" />
                      Upload Photo
                    </span>
                  </label>

                  {avatarPreview ? (
                    <button
                      className="flex items-center justify-center gap-2 rounded-[18px] border border-[#e6ddd4] px-4 py-2.5 text-[13px] font-semibold text-[#7f736b] transition hover:bg-[#faf7f3] sm:text-sm"
                      type="button"
                      onClick={handleRemoveAvatar}
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove Photo
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {profileError ? (
            <div className="rounded-[18px] border border-[#f0d4cb] bg-[#fff8f6] px-4 py-2.5 text-[13px] text-[#b4533f] sm:text-sm">
              {profileError}
            </div>
          ) : null}

          {profileMessage ? (
            <div className="rounded-[18px] border border-[#e6ddd4] bg-[#fcfbf9] px-4 py-2.5 text-[13px] text-[#6f6258] sm:text-sm">
              {profileMessage}
            </div>
          ) : null}

          <button
            className="w-full rounded-[16px] border border-[#e2d9d0] bg-[#f4f1ed] px-5 py-2.5 text-[15px] font-semibold text-[#264573] transition hover:bg-[#ece7e1] disabled:cursor-not-allowed disabled:opacity-70 sm:text-base"
            type="submit"
            disabled={profileSubmitting}
          >
            {profileSubmitting ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      </div>
    );
  }

  function renderSecuritySection() {
    return (
      <div className="rounded-[22px] border border-[#efe7df] bg-[#fbf9f6] p-3.5 shadow-[0_10px_22px_rgba(17,17,17,0.04)] sm:p-4">
        <div className="rounded-[20px] border border-[#e6ddd4] bg-white px-4 py-2.5 shadow-[0_8px_18px_rgba(17,17,17,0.04)]">
          <div className="flex items-center gap-3">
            <button
              className="flex h-9 w-9 items-center justify-center rounded-[16px] border border-[#e6ddd4] text-[#7f8fa5] transition hover:bg-[#faf7f3]"
              type="button"
              onClick={() => setActiveSection('menu')}
              aria-label="Back to settings sections"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <h4 className="text-[15px] font-semibold text-[#1f293d] sm:text-[16px]">Account Security</h4>
              <p className="text-[13px] text-[#6f7f95] sm:text-sm">Manage and update this settings section.</p>
            </div>
          </div>
        </div>

        <form className="mt-3.5 grid gap-2.5" onSubmit={handlePasswordSubmit}>
          <PasswordField
            description="Current Password"
            name="currentPassword"
            type="password"
            value={passwordValues.currentPassword}
            onChange={handlePasswordChange}
            onToggleVisibility={togglePasswordVisibility}
            placeholder="Enter current password"
            visible={passwordVisibility.currentPassword}
          />

          <PasswordField
            description="New Password"
            name="newPassword"
            type="password"
            value={passwordValues.newPassword}
            onChange={handlePasswordChange}
            onToggleVisibility={togglePasswordVisibility}
            placeholder="Create a new password"
            visible={passwordVisibility.newPassword}
          />

          <PasswordField
            description="Confirm Password"
            name="confirmPassword"
            type="password"
            value={passwordValues.confirmPassword}
            onChange={handlePasswordChange}
            onToggleVisibility={togglePasswordVisibility}
            placeholder="Confirm new password"
            visible={passwordVisibility.confirmPassword}
          />

          {passwordError ? (
            <div className="rounded-[18px] border border-[#f0d4cb] bg-[#fff8f6] px-4 py-2.5 text-[13px] text-[#b4533f] sm:text-sm">
              {passwordError}
            </div>
          ) : null}

          {passwordMessage ? (
            <div className="rounded-[18px] border border-[#e6ddd4] bg-[#fcfbf9] px-4 py-2.5 text-[13px] text-[#6f6258] sm:text-sm">
              {passwordMessage}
            </div>
          ) : null}

          <button
            className="mt-1 w-full rounded-[16px] border border-[#e2d9d0] bg-[#f4f1ed] px-5 py-2.5 text-[15px] font-semibold text-[#264573] transition hover:bg-[#ece7e1] disabled:cursor-not-allowed disabled:opacity-70 sm:text-base"
            type="submit"
            disabled={passwordSubmitting}
          >
            {passwordSubmitting ? 'Changing Password...' : 'Change Password'}
          </button>
        </form>
      </div>
    );
  }

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[rgba(24,33,48,0.28)] px-3 py-3 backdrop-blur-[3px] sm:px-4 sm:py-6">
      <div className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-[640px] flex-col overflow-hidden rounded-[24px] border border-[#ece4db] bg-[linear-gradient(180deg,#fbfaf8_0%,#f8f5f1_100%)] shadow-[0_24px_56px_rgba(15,23,42,0.20)] sm:max-h-[calc(100dvh-3rem)] sm:rounded-[26px]">
        <div className="flex items-start justify-between gap-3 border-b border-[#e8e0d8] px-4 py-3.5 sm:gap-4 sm:px-5 sm:py-4">
          <div className="min-w-0 flex-1">
            <div className="inline-flex max-w-full rounded-full border border-[#e6ddd4] bg-white px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#7083a3] shadow-[0_8px_18px_rgba(17,17,17,0.04)] sm:px-3.5 sm:py-1.5 sm:text-[11px] sm:tracking-[0.26em]">
              Workspace Controls
            </div>
            <h3 className="mt-3 text-[1.45rem] font-semibold tracking-[-0.02em] text-[#1d2740] sm:text-[1.6rem]">
              Settings
            </h3>
            <p className="mt-1 max-w-[500px] text-[14px] leading-6 text-[#6c7c95] sm:text-[15px] sm:leading-6">
              Refine profile and security preferences from one place.
            </p>
            {user?.must_change_password ? (
              <div className="mt-3 flex w-full items-start gap-2.5 rounded-[20px] border border-[#f1d1c6] bg-[#fff7f4] px-4 py-2 text-[13px] font-medium leading-6 text-[#b4533f] sm:items-center sm:text-sm">
                <ShieldCheck className="mt-1 h-4 w-4 shrink-0 sm:mt-0" />
                <span>You are still using a temporary password. Update it when you are ready.</span>
              </div>
            ) : null}
          </div>

          <div className="relative hidden sm:block">
            <div className="rounded-[18px] border border-[#ece4db] bg-white p-2.5 shadow-[0_12px_22px_rgba(17,17,17,0.05)]">
              <div className="flex h-12 w-12 items-center justify-center rounded-[16px] bg-[#6f5644] text-white shadow-[0_12px_22px_rgba(111,86,68,0.18)]">
                <ShieldCheck className="h-6 w-6" />
              </div>
            </div>
            <button
              className="absolute -right-3.5 -top-3.5 flex h-10 w-10 items-center justify-center rounded-full border border-[#ece4db] bg-white text-[#7d8ca3] shadow-[0_10px_20px_rgba(17,17,17,0.05)] transition hover:bg-[#faf7f3]"
              type="button"
              onClick={requestClose}
              aria-label="Close settings modal"
            >
              <X className="h-4.5 w-4.5" />
            </button>
          </div>

          <div className="sm:hidden">
            <button
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#ece4db] bg-white text-[#7d8ca3] shadow-[0_10px_20px_rgba(17,17,17,0.05)] transition hover:bg-[#faf7f3]"
              type="button"
              onClick={requestClose}
              aria-label="Close settings modal"
            >
              <X className="h-4.5 w-4.5" />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3.5 sm:px-5 sm:py-4">
          {activeSection === 'menu' ? null : (
            <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-[#98a7bc] sm:mb-3.5 sm:text-xs">
              {activeSection === 'profile' ? 'Profile Settings' : 'Security Settings'}
            </div>
          )}

          {activeSection === 'menu' ? renderSettingsMenu() : null}
          {activeSection === 'profile' ? renderProfileSection() : null}
          {activeSection === 'security' ? renderSecuritySection() : null}
        </div>
      </div>

      <ConfirmationModal
        isOpen={isDiscardModalOpen}
        title="Discard Changes"
        description="You have unsaved settings changes. Are you sure you want to close this modal?"
        confirmLabel="Discard"
        onConfirm={confirmDiscardChanges}
        onCancel={() => setIsDiscardModalOpen(false)}
      />
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default ProfileModal;
